import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule, Injector } from '@angular/core';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { authInterceptor } from './core/http/auth.interceptor';
import { formEncodeInterceptor } from './core/http/form-encode.interceptor';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';

import { AppRoutingModule, WildcardRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppService } from './app.service';
import { AuthBootstrapService } from './core/http/auth-bootstrap.service';
import { GlobalModule } from './global/global.module';
import { CoreModule } from './core/core.module';
import { WorkspaceModule } from './workspace/workspace.module';

import { ScreenVideoModule } from './screen-video-container/screen-video.module';
import { TrialBalanceInventoryModule } from './trial-balance-inventory/trial-balance-inventory.module';
import { HomePageModule } from './home-page/home-page.module';
import { NavigationModule } from './navigation/navigation.module';
import { HeaderModule } from './header/header.module';
import { ScreensModule } from './screens/screens.module';
import { AppFooterModule } from './app-footer/app-footer.module';
import { EfileModule } from './efile/efile.module';
import { DSTModule } from './DST/dst.module';
import { SharedComponentsModule } from './shared/components/shared-components.module';
import { SharedDirectivesModule } from './shared/_directives/shared-directives.module';
import { GhostConsoleModule } from './ghost-console/ghost-console.module';
import { ScreenDocsModule } from './screen-docs/screen-docs.module';
import { ReleaseModule } from './release-notes/release-notes.module';
import { SharedPipesModule } from './shared/pipes/shared-pipe.module';
import { DomesticModule } from './domestic/domestic.module';
import { ForeignModule } from './foreign/foreign.module';
import { FtcModule } from './ftc/ftc.module';
import { GhostModule } from './ghost/ghost.module';
import { AdminModule } from './admin/admin.module';
import { PartnershipModule } from './partnership/partnership.module';
import { DiagnosticDashboardModule } from './diagnostic-dashboard/diagnostic-dashboard.module';
import { CBCModule } from './cbc/cbc.module';
import { UsersComponentModule } from './users-component/users-component.module';
import { F1120Module } from './F1120/f1120.module';
import { EntityClassificationActionsModule } from './entity-classification/entity-classification.module';
import { PagesModule } from './pages/pages.module';
import { StandaloneRoutesModule } from './standalone-routes.module';
import { NewUpdateScreenModule } from './new-update-screen/new-update-screen.module';
import { registerGridCustomElements } from './core/grid/ce-registration';
import { GridModalsModule } from './core/grid/grid-modals.module';
import { WorkspaceScreenComponent } from './workspace/workspace-screen.component';
import { ExDashboardModule } from './core/executive-dashboards/ex-dashboard.module';
import { NewProformaModule } from './newProforma/newProforma.module';
import { RecurringEpModule } from './recurring-ep/recurring-ep.module';
import { F965BModule } from './F965B/f965b.module';
import { F4626Module } from './F4626/f4626.module';
import { SltApiDashboardModule } from './slt-api-dashboard/slt-api-dashboard.module';
import { GtwFormsModule } from './Forms/forms.module';
import { FirForm8865Module } from './FIR-Form8865/fir-form8865.module';
import { Fir8858Module } from './FIR-8858/fir-8858.module';
import { WwniSubCategoryModule } from './wwni-sub-category/wwni-sub-category.module';
import { RelatedPartyPaymentModule } from './related-party-payment/related-party-payment.module';
import { SltDashboardModule } from './slt-dashboard/slt-dashboard.module';
import { StateEntityGroupsModule } from './state-entity-groups/state-entity-groups.module';
import { Section965Module } from './section965/section965.module';
import { ReviewModule } from './review/review.module';
import { WorkflowModule } from './workflow/workflow.module';

export function initializeApp(authBootstrapService: AuthBootstrapService, appService: AppService) {
  return () =>
    authBootstrapService.ensureAuthenticated().then((isAuthenticated: boolean) => {
      if (!isAuthenticated) {
        return Promise.resolve();
      }
      return appService.callApis();
    });
}

@NgModule({ declarations: [AppComponent, WorkspaceScreenComponent],
    bootstrap: [AppComponent],
    exports: [], imports: [BrowserModule,
        BrowserAnimationsModule,
        CommonModule,
        MatSnackBarModule,
        MatDialogModule,
        MatTabsModule,
        AppRoutingModule,
        GlobalModule,
        CoreModule,
        WorkspaceModule,
        ScreenVideoModule,
        TrialBalanceInventoryModule,
        HomePageModule,
        NavigationModule,
        HeaderModule,
        ScreensModule,
        AppFooterModule,
        EfileModule,
        DSTModule,
        SharedComponentsModule,
        SharedDirectivesModule,
        GhostConsoleModule,
        ScreenDocsModule,
        ReleaseModule,
        SharedPipesModule,
        DomesticModule,
        ForeignModule,
        FtcModule,
        GhostModule,
        AdminModule,
        ExDashboardModule,
        PartnershipModule,
        DiagnosticDashboardModule,
        CBCModule,
        UsersComponentModule,
        F1120Module,
        EntityClassificationActionsModule,
        NewUpdateScreenModule,
        NewProformaModule,
        RecurringEpModule,
        F965BModule,
        F4626Module,
        SltApiDashboardModule,
        GtwFormsModule,
        FirForm8865Module,
        Fir8858Module,
        WwniSubCategoryModule,
        RelatedPartyPaymentModule,
        SltDashboardModule,
        StateEntityGroupsModule,
        Section965Module,
        ReviewModule,
        WorkflowModule,
        GridModalsModule,
        PagesModule,
        StandaloneRoutesModule,
        WildcardRoutingModule], providers: [
        AppService,
        {
            provide: APP_INITIALIZER,
            useFactory: initializeApp,
            deps: [AuthBootstrapService, AppService],
            multi: true,
        },
        provideHttpClient(withInterceptors([formEncodeInterceptor, authInterceptor]), withXhr()),
    ] })
export class AppModule {
  constructor(private injector: Injector) {
    registerGridCustomElements(this.injector);
  }
}
